import { hasUserPermission } from '@glyphic/user-api-types'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { notifyBackendUnavailable } from '../api/authInterceptors'
import { getAuthMe } from '../api/auth'
import { isNetworkRequestError, rethrowIfUnauthorized } from '../api/errors'
import { getAuthToken } from '../lib/session'
import {
  clearUserAuthorization,
  readStoredUserPermissions,
  readStoredUserRoles,
  writeUserAuthorization,
} from '../lib/userAuthorization'

type UserAuthorizationValue = {
  roles: string[]
  permissions: string[]
  loaded: boolean
  hasPerm: (code: string | undefined) => boolean
  refresh: () => Promise<void>
  setFromLogin: (roles: string[], permissions: string[]) => void
  clear: () => void
}

const UserAuthorizationContext = createContext<UserAuthorizationValue | null>(null)

export function UserAuthorizationProvider({ children }: { children: ReactNode }) {
  const [roles, setRoles] = useState<string[]>(() => readStoredUserRoles())
  const [permissions, setPermissions] = useState<string[]>(() => readStoredUserPermissions())
  const [loaded, setLoaded] = useState(false)

  const applyProfile = useCallback((nextRoles: string[], nextPermissions: string[]) => {
    setRoles(nextRoles)
    setPermissions(nextPermissions)
    writeUserAuthorization({ roles: nextRoles, permissions: nextPermissions })
  }, [])

  const clear = useCallback(() => {
    setRoles([])
    setPermissions([])
    clearUserAuthorization()
  }, [])

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      clear()
      setLoaded(true)
      return
    }
    try {
      const me = await getAuthMe()
      applyProfile(me.roles ?? [], me.permissions ?? [])
      setLoaded(true)
    } catch (e) {
      rethrowIfUnauthorized(e)
      if (isNetworkRequestError(e)) {
        notifyBackendUnavailable()
        return
      }
      /* 非 401 / 网络错误时沿用本地缓存 */
      setLoaded(true)
    }
  }, [applyProfile, clear])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<UserAuthorizationValue>(
    () => ({
      roles,
      permissions,
      loaded,
      hasPerm: (code) => hasUserPermission(permissions, code),
      refresh,
      setFromLogin: applyProfile,
      clear,
    }),
    [roles, permissions, loaded, refresh, applyProfile, clear],
  )

  return (
    <UserAuthorizationContext.Provider value={value}>{children}</UserAuthorizationContext.Provider>
  )
}

export function useUserAuthorization(): UserAuthorizationValue {
  const ctx = useContext(UserAuthorizationContext)
  if (!ctx) {
    throw new Error('useUserAuthorization 须在 UserAuthorizationProvider 内使用')
  }
  return ctx
}
